import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "@/index.css";
import "@/i18n";
import App from "@/App";

// Suppress the benign "ResizeObserver loop completed with undelivered
// notifications" warning. It fires when a ResizeObserver callback triggers a
// layout change that would cause the observer to fire again in the same
// frame. Chrome/Firefox log this as an uncaught error which the
// webpack-dev-server overlay then displays as a giant scary red dialog —
// even though nothing is actually broken. Radix/shadcn primitives (dialogs,
// popovers, dropdowns) commonly trigger this on open/close.
// This handler swallows only this specific message; everything else still
// bubbles up normally. Production builds don't have the overlay so this is
// preview-only noise, but the handler is harmless there too.
//
// IMPORTANT: `true` (capture phase) is required so we intercept BEFORE
// webpack-dev-server's overlay handler (which listens in bubble phase).
const RESIZE_OBSERVER_WARN = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
const isResizeObserverNoise = (msg) => typeof msg === "string" && RESIZE_OBSERVER_WARN.test(msg);
window.addEventListener(
  "error",
  (e) => {
    const msg = (e && e.message) || (e && e.error && e.error.message) || "";
    if (isResizeObserverNoise(msg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  },
  true,
);
window.addEventListener(
  "unhandledrejection",
  (e) => {
    const msg = e && e.reason && (e.reason.message || String(e.reason));
    if (isResizeObserverNoise(msg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  },
  true,
);
// Also patch console.error so React DevTools' error overlay doesn't surface it
const originalConsoleError = window.console.error;
window.console.error = function (...args) {
  if (args && args.length && isResizeObserverNoise(args[0])) {
    return;
  }
  originalConsoleError.apply(window.console, args);
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>,
);
